import { Button } from "@/components/ui/button";
import { useApp } from "@/hooks/use-app-context";
import { Icon } from "@/lib/icons";
import type { SettingsCopy } from "./settings-copy";
import { SettingsSheet } from "./SettingsSheet";
import type { SettingsState } from "./use-settings";

/** GDPR: what the export contains, the export request, and the user's rights. */
export function PrivacySheet({ state, copy }: { state: SettingsState; copy: SettingsCopy }) {
  const { t, user, language } = useApp();
  const included = [t.includedAudioRecordings, t.includedImages, t.includedTranscriptions, t.includedValidations, t.includedMetadata];
  const rights = [t.rightOfAccess, t.rightToRectification, t.rightToErasure, t.rightToPortability, t.rightToObject];

  return (
    <SettingsSheet
      open={state.sheet === "privacy"}
      onClose={state.closeSheet}
      title={copy.myData}
      description={copy.privacySheetDesc}
      footer={
        <Button onClick={() => void state.requestExport()} disabled={state.isDownloading || !user?.id} className="w-full sm:w-auto">
          {state.isDownloading ? <Icon name="loader2" className="animate-spin" /> : <Icon name="download" />}
          {state.isDownloading ? t.requesting : t.requestDataExport}
        </Button>
      }
    >
      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-foreground">{t.whatIsIncluded}</h3>
        <ul className="space-y-1.5">
          {included.map((item) => (
            <li key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
              <Icon name="check" className="mt-0.5 size-4 shrink-0 text-success" />
              {item}
            </li>
          ))}
        </ul>
        <p className="rounded-md bg-surface-2 px-3 py-2 text-xs leading-5 text-muted-foreground">
          {t.estimatedTime}. {t.emailDelivery}
        </p>
      </section>

      <section className="space-y-2 border-t border-border/60 pt-5">
        <h3 className="text-sm font-semibold text-foreground">{copy.rightsTitle}</h3>
        <ul className="space-y-2">
          {rights.map((right) => {
            const [name, ...rest] = right.split(":");
            return (
              <li key={right} className="text-sm leading-6 text-muted-foreground">
                {rest.length > 0 ? (
                  <>
                    <span className="font-medium text-foreground">{name.trim()}</span>
                    {language === "fr" ? " : " : ": "}
                    {rest.join(":").trim()}
                  </>
                ) : (
                  right
                )}
              </li>
            );
          })}
        </ul>
        <p className="pt-1 text-sm text-muted-foreground">
          {t.questionsContact}{" "}
          <a href="mailto:privacy@yodi.tg" className="font-medium text-brand underline-offset-4 hover:underline">
            privacy@yodi.tg
          </a>
        </p>
      </section>
    </SettingsSheet>
  );
}
